export const BIOTRON_SETTINGS_FIELD_ORDER = Object.freeze([
  "plantBpm", "lightBpm", "noteOffPercent", "noteDistance", "firstValue",
  "smoothness", "scale", "minPlantVelocity", "maxPlantVelocity",
  "minLightVelocity", "maxLightVelocity", "randomness", "same_note_plant",
  "same_note_light", "range_light_note", "light_pitch_mode",
  "plant_no_velocity", "light_no_velocity", "randomPlantVelocity",
  "randomLightVelocity", "performance", "middle_plant_note",
  "plant_midi_channel", "light_midi_channel", "swing_first_note_percent",
  "button_mode_state"
])

export const BIOTRON_SETTINGS_QUERY_ID = 123
export const BIOTRON_SETTINGS_PROTOCOL_VERSION = 1
export const BIOTRON_SETTINGS_SCHEMA_VERSION = 1
export const BIOTRON_SETTINGS_REPLY_BYTES = 47

const booleanFields = new Set([
  "randomness", "light_pitch_mode", "plant_no_velocity",
  "light_no_velocity", "randomPlantVelocity", "randomLightVelocity",
  "performance", "button_mode_state"
])

const integer = (value, minimum, maximum) => {
  const number = Number(value)
  if (!Number.isFinite(number)) return minimum
  return Math.min(maximum, Math.max(minimum, Math.round(number)))
}

const commandValue = (commands, name) => {
  const command = commands && commands[name]
  return command && typeof command === "object" && "value" in command
    ? command.value
    : command
}

export function biotronSettingsVector(commands) {
  const bpm = integer(commandValue(commands, "plantBpm"), 0, 16383)
  const values = [bpm & 0x7f, bpm >> 7]
  for (const name of BIOTRON_SETTINGS_FIELD_ORDER.slice(1)) {
    const value = commandValue(commands, name)
    values.push(booleanFields.has(name) ? (value ? 1 : 0) : integer(value, 0, 127))
  }
  return values
}

export function biotronCommandsFromSettingsReply(reply) {
  if (!reply || reply.source !== 1 || !reply.sourceValid || !Array.isArray(reply.values) ||
      reply.values.length !== BIOTRON_SETTINGS_FIELD_ORDER.length + 1) return null

  const commands = {plantBpm: reply.values[0] + (reply.values[1] << 7)}
  BIOTRON_SETTINGS_FIELD_ORDER.slice(1).forEach((name, index) => {
    const value = reply.values[index + 2]
    commands[name] = booleanFields.has(name) ? Boolean(value) : value
  })
  return commands
}

const decodeU32 = (bytes, offset) => {
  let value = 0
  for (let byte = 0; byte < 5; byte++) value += bytes[offset + byte] * (2 ** (7 * byte))
  return value
}

export function parseBiotronSettingsReply(data) {
  const bytes = Array.from(data || [])
  if (bytes.length !== BIOTRON_SETTINGS_REPLY_BYTES || bytes[0] !== 0xf0 || bytes[1] !== 0x14 ||
      bytes[2] !== 0x0d || bytes.at(-1) !== 0xf7) return null
  const payload = bytes.slice(3, -1)
  if (payload.some(byte => !Number.isInteger(byte) || byte < 0 || byte > 0x7f) ||
      payload[0] !== BIOTRON_SETTINGS_QUERY_ID ||
      payload[1] !== BIOTRON_SETTINGS_PROTOCOL_VERSION ||
      payload[2] !== BIOTRON_SETTINGS_SCHEMA_VERSION ||
      payload[3] > 1 || (payload[5] & ~3) !== 0) return null
  return {
    source: payload[3],
    requestId: payload[4],
    sourceValid: (payload[5] & 1) !== 0,
    dirty: (payload[5] & 2) !== 0,
    dirtyGeneration: decodeU32(payload, 6),
    persistedGeneration: decodeU32(payload, 11),
    values: payload.slice(16)
  }
}

export function biotronSettingsAreSaved(reply, expectedValues) {
  return Boolean(reply && reply.source === 1 && reply.sourceValid && !reply.dirty &&
    reply.dirtyGeneration === reply.persistedGeneration &&
    Array.isArray(expectedValues) && reply.values.length === expectedValues.length &&
    reply.values.every((value, index) => value === expectedValues[index]))
}
